// the keys here should match those in constants/pageTypes.js
//
// key: internal page type; value: GA page type
export const GA_PAGE_TYPES = {
  author: 'Author Index',
  archive: 'Archive Index',
  category: 'Category Index (née Vertical Index)',
  collection: 'Tag Index (née Category Index)',
  feature: 'Feature Page',
  home: 'Home Page',
  homemade: 'Homemade',
  page: 'Standalone Page',
  post: 'Post Page',
  error404: '404 Page',
  error: 'Error Page',
  preview: 'Preview Page',
  email: 'Email Page',
  search: 'Site Search',
  account: 'My Account',
  savedStories: 'My Account: Saved Stories',
  visionBoards: 'My Account: Vision Boards',
  visionBoard: 'My Account: Vision Board',
  prismImage: 'Prism Image Page',
  prismTag: 'Prism Tag Page',
  prismColor: 'Prism Color Page',
  authentication: 'Not Authorized Page',
  essentialProducts: 'Essential Products Page',
  faq: 'FAQ Page',
  contest: 'Contest Page',
  submission: 'Submission Page',
  staticPrismPage: 'Prism Landing Page',
  smallCoolExperience: 'Small Cool Experience',
  imageCollection: 'Image Collections Page',
  recipeBox: 'Recipe Box',
  kitchnFoodFest2020: 'Kitchn Food Fest 2020'
};

/**
 * when passed an internal page type, returns a GA-specific page type. defaults
 * to 'Not Set' for unknown page types.
 *
 * @export
 * @param {String} [pageType=''] internal page type
 * @returns page type for GA reporting
 */
export function getGaPageType(pageType = '') {
  return GA_PAGE_TYPES[pageType] || 'Not Set';
}

// keys are bundle item type; value is name of WP Block (specifically, how we
// want it to appear in GA).
//
// this list includes only the WP blocks that are prefixed with 'AT Media', and
// the values are the block name w/o the 'AT Media' prefix.
//
// NOTE: the only embed type not present here is "Post Link Roundup" because it
// gets transformed to the 'html' type.
export const BUNDLE_ITEM_TYPE_MAP = {
  emailSignup: 'Email Signup Block',
  faq: 'FAQ Content',
  imageSlider: 'Image Slider',
  listicle: 'Listicle',
  recipe: 'Recipes',
  recipeCollection: 'Recipe Round Up',
  subsectionNav: 'Recipe Round Up Menu',
  product: 'Product Listing', // legacy embed; uses product-listings service
  productEmbed: 'Shop: Product',
  productTeaser: 'Shop: Single Product',
  productTeaserList: 'Shop: Product List',
  sideBySide: 'Side By Side',
  tip: 'Tip Block',

  // many WP blocks map to these.
  // NOTE: item.coverMedia is set if used in that context.
  gallery: 'Cloudinary Gallery',
  image: 'Cloudinary Image',
  video: 'JW Video',

  // youtube, video, iframes, etc.
  // NOTE: item.data.provider is set for ceros + curalate embeds
  thirdParty: 'Third Party Embed'
};

/**
 * formats and filters the bundleItemTypes data for inclusion in GA.
 *
 * - maps keys of bundleItemTypes to editorially-understandable values for
 *   clearer reporting. values are left alone.
 *
 * - includes only the types specified in BUNDLE_ITEM_TYPE_MAP
 *
 * @export
 * @param {Object} bundleItemTypes keys are types; values are count of type
 * @returns {Object} bundleItemTypes, ready for reporting to GA
 */
export function preparePostBundleItemTypes(bundleItemTypes = {}) {
  const allowedTypes = Object.keys(BUNDLE_ITEM_TYPE_MAP);

  const unsortedBundleItemTypes = Object.entries(bundleItemTypes).reduce(
    (acc, [k, v]) =>
      allowedTypes.includes(k) ? { ...acc, [BUNDLE_ITEM_TYPE_MAP[k]]: v } : acc,
    {}
  );

  return Object.keys(unsortedBundleItemTypes)
    .sort()
    .reduce(
      (acc, curr) => ({
        ...acc,
        [curr]: unsortedBundleItemTypes[curr]
      }),
      {}
    );
}
